import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "lucide-react";
import { useSupabaseAuth } from "@/contexts/SupabaseAuthContext";
import { usePasswordValidation } from "@/hooks/usePasswordValidation";
import { toast } from "sonner";

const RegisterForm = () => {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { signup } = useSupabaseAuth();
  
  const {
    password,
    confirmPassword,
    showPassword,
    showConfirmPassword,
    passwordStrong,
    passwordsMatch,
    setPassword,
    setConfirmPassword,
    togglePasswordVisibility,
    toggleConfirmPasswordVisibility,
  } = usePasswordValidation();

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateEmail(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!passwordsMatch || !passwordStrong) {
      toast.error("Please check your password requirements");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const { error } = await signup(email, password, username);
      
      if (error) {
        toast.error(error);
      } else {
        toast.success("Registration successful! Please check your email to confirm your account.");
        // Clear form
        setEmail("");
        setUsername("");
        setPassword("");
        setConfirmPassword("");
      }
    } catch (err) {
      toast.error("An unexpected error occurred during registration");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block font-semibold text-white mb-1">Email</label>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="bg-white/10 text-white border-white/20 placeholder:text-white/50"
          placeholder="Enter your email"
        />
      </div>

      <div>
        <label className="block font-semibold text-white mb-1">Username</label>
        <Input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          className="bg-white/10 text-white border-white/20 placeholder:text-white/50"
          placeholder="Choose a username"
        />
      </div>
      
      <div>
        <label className="block font-semibold text-white mb-1">Password</label>
        <div className="relative">
          <Input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="bg-white/10 text-white border-white/20 placeholder:text-white/50 pr-10"
            placeholder="Create a secure password"
          />
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/70 hover:text-white"
          >
            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>
        {password && !passwordStrong && (
          <p className="text-xs text-red-500 mt-1">
            Password must be at least 8 characters with a mix of uppercase, lowercase, numbers, or special characters
          </p>
        )}
      </div>

      <div>
        <label className="block font-semibold text-white mb-1">Confirm Password</label>
        <div className="relative">
          <Input
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className="bg-white/10 text-white border-white/20 placeholder:text-white/50 pr-10"
            placeholder="Enter password again"
          />
          <button
            type="button"
            onClick={toggleConfirmPasswordVisibility}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/70 hover:text-white"
          >
            {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>
        {!passwordsMatch && confirmPassword && (
          <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
        )}
      </div>

      <Button 
        type="submit" 
        className="w-full bg-purple-600 hover:bg-purple-700 text-white" 
        disabled={submitting || !passwordsMatch || !passwordStrong}
      >
        {submitting ? "Creating Account..." : "Create Account"}
      </Button>
    </form>
  );
};

export default RegisterForm;
